/**
 * TicketsPage — cartelera global (pagafacil.eu/entradas)
 *
 * Estilo IES: dark editorial + rojo #E11A1A + tipografía condensada
 * (Archivo). Hero corto con "CARTELERA", grid de posters 3/4, franja
 * inferior con reclamo "cobros colectivos → entradas".
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/client';

interface Show {
  id: string;
  slug: string;
  title: string;
  subtitle?: string;
  poster_url?: string | null;
  artist_name?: string | null;
  event_datetime: string;
  doors_open_at?: string | null;
  venue_name?: string | null;
  venue_city?: string | null;
  ticket_price?: number | null;
  ticket_price_min?: number | null;
  ticket_price_max?: number | null;
  capacity?: number | null;
  tickets_available?: number | null;
  status: string;
}

const ACCENT = '#E11A1A';

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
}
function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
}
function priceLabel(s: Show): string {
  if (s.ticket_price != null) return `${Number(s.ticket_price).toFixed(2)} €`;
  if (s.ticket_price_min != null && s.ticket_price_max != null && s.ticket_price_min !== s.ticket_price_max) {
    return `desde ${Number(s.ticket_price_min).toFixed(2)} €`;
  }
  if (s.ticket_price_min != null) return `${Number(s.ticket_price_min).toFixed(2)} €`;
  return 'A consultar';
}

export default function TicketsPage() {
  const [shows, setShows] = useState<Show[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'Cartelera · Pagafácil';
    api.get<Show[]>('/public/shows')
      .then((r) => setShows(r.data))
      .catch((e) => setError(e?.response?.data?.detail || 'No se pudo cargar la cartelera.'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <Shell>
      {/* HERO */}
      <section style={{
        minHeight: '60vh',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        textAlign: 'center', padding: '140px 24px 60px',
        position: 'relative', overflow: 'hidden',
        background: '#0a0a0a',
      }}>
        <div style={{
          position: 'absolute', inset: 0,
          background: `radial-gradient(ellipse at 30% 30%, ${ACCENT}18 0%, transparent 50%),
                       radial-gradient(ellipse at 70% 70%, ${ACCENT}12 0%, transparent 50%),
                       linear-gradient(180deg, transparent 0%, rgba(10,10,10,0.9) 100%)`,
        }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{
            fontFamily: 'JetBrains Mono, monospace', fontSize: 12, letterSpacing: 6,
            textTransform: 'uppercase', color: ACCENT, marginBottom: 24,
          }}>
            Pagafácil · Entradas
          </div>
          <h1 style={{
            fontFamily: 'Archivo, sans-serif',
            fontSize: 'clamp(48px, 9vw, 110px)', fontWeight: 900, lineHeight: 0.95,
            letterSpacing: '0.04em', textTransform: 'uppercase',
            margin: 0, color: '#FFF',
            textShadow: `0 2px 40px ${ACCENT}22`,
          }}>
            Cartelera
          </h1>
          <p style={{
            fontFamily: 'DM Sans, sans-serif', fontSize: 17, lineHeight: 1.6,
            color: '#f5f0e8', opacity: 0.75, margin: '32px auto 0', maxWidth: 560,
          }}>
            Compra nominativa con QR al móvil. Pago con Bizum o tarjeta.
            Devolución garantizada si el evento se cancela.
          </p>
          <div style={{ width: 80, height: 3, background: ACCENT, margin: '40px auto 0' }} />
        </div>
      </section>

      {/* GRID */}
      <section style={{ padding: '80px 24px 100px', background: '#0a0a0a' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          {loading && (
            <div style={{ textAlign: 'center', color: '#888', fontFamily: 'JetBrains Mono, monospace', fontSize: 12, letterSpacing: 2, textTransform: 'uppercase' }}>
              Cargando cartelera…
            </div>
          )}
          {error && (
            <div style={{ textAlign: 'center', color: ACCENT, fontFamily: 'JetBrains Mono, monospace', fontSize: 13 }}>
              {error}
            </div>
          )}
          {!loading && !error && shows.length === 0 && (
            <div style={{ padding: 80, textAlign: 'center', border: '1px solid #222' }}>
              <div style={{
                fontFamily: 'Archivo, sans-serif', fontSize: 28, fontWeight: 900,
                letterSpacing: 2, textTransform: 'uppercase', color: '#f5f0e8', marginBottom: 12,
              }}>
                Sin eventos ahora mismo
              </div>
              <div style={{ color: '#888', fontFamily: 'DM Sans, sans-serif' }}>
                Anunciamos nuevos eventos cada semana — vuelve pronto.
              </div>
            </div>
          )}

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 24,
          }}>
            {shows.map((s) => <ShowCard key={s.id} show={s} />)}
          </div>
        </div>
      </section>

      {/* FRANJA "COBROS COLECTIVOS" */}
      <section style={{
        padding: '80px 24px',
        background: `radial-gradient(ellipse at 50% 50%, ${ACCENT}0A 0%, transparent 60%), #111`,
        borderTop: '1px solid #1a1a1a',
        borderBottom: '1px solid #1a1a1a',
        textAlign: 'center',
      }}>
        <div style={{
          fontFamily: 'JetBrains Mono, monospace', fontSize: 11, letterSpacing: 4,
          textTransform: 'uppercase', color: ACCENT, marginBottom: 16,
        }}>
          Y también
        </div>
        <h2 style={{
          fontFamily: 'Archivo, sans-serif', fontSize: 'clamp(28px, 4.5vw, 48px)',
          fontWeight: 900, letterSpacing: '0.05em', textTransform: 'uppercase',
          margin: 0, color: '#FFF', maxWidth: 700, marginLeft: 'auto', marginRight: 'auto',
        }}>
          Cobros colectivos sin liarse
        </h2>
        <p style={{
          fontFamily: 'DM Sans, sans-serif', fontSize: 16, lineHeight: 1.7,
          color: '#f5f0e8', opacity: 0.75, margin: '24px auto 32px', maxWidth: 560,
        }}>
          Comparte un enlace por WhatsApp y cada uno paga su parte.
          Bizum o tarjeta · sin app · sin login.
        </p>
        <Link to="/nuevo" style={{
          display: 'inline-block', padding: '16px 40px',
          border: `2px solid ${ACCENT}`, color: '#FFF',
          fontFamily: 'Archivo, sans-serif', fontSize: 12, letterSpacing: 5,
          textTransform: 'uppercase', textDecoration: 'none',
          transition: 'all 0.3s',
        }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = ACCENT; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
        >
          Crear un cobro
        </Link>
      </section>

      <DarkFooter />
    </Shell>
  );
}

function ShowCard({ show }: { show: Show }) {
  const soldOut = show.status === 'sold_out' || (show.tickets_available != null && show.tickets_available <= 0);
  const cancelled = show.status === 'cancelled';

  return (
    <Link to={`/e/${show.slug}`} style={{
      display: 'flex', flexDirection: 'column', textDecoration: 'none', color: 'inherit',
      background: '#111', border: '1px solid #222', overflow: 'hidden',
      transition: 'all 0.35s ease', position: 'relative',
    }}
      onMouseEnter={(e) => {
        const el = e.currentTarget as HTMLElement;
        el.style.transform = 'translateY(-6px)';
        el.style.borderColor = ACCENT;
        el.style.boxShadow = `0 24px 60px -20px ${ACCENT}55`;
      }}
      onMouseLeave={(e) => {
        const el = e.currentTarget as HTMLElement;
        el.style.transform = 'translateY(0)';
        el.style.borderColor = '#222';
        el.style.boxShadow = 'none';
      }}
    >
      <div style={{
        aspectRatio: '3/4',
        background: show.poster_url ? `url(${show.poster_url}) center/cover` : '#0a0a0a',
        position: 'relative',
      }}>
        {!show.poster_url && (
          <div style={{
            height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: ACCENT, fontFamily: 'Archivo, sans-serif', fontSize: 22, fontWeight: 900,
            letterSpacing: 2, textTransform: 'uppercase', textAlign: 'center', padding: 24,
          }}>
            {show.title}
          </div>
        )}
        {(cancelled || soldOut) && (
          <div style={{
            position: 'absolute', top: 12, right: 12,
            background: cancelled ? '#c0392b' : '#0a0a0a',
            color: '#FFF', padding: '6px 12px',
            fontFamily: 'JetBrains Mono, monospace', fontSize: 10,
            letterSpacing: 2, textTransform: 'uppercase', fontWeight: 700,
          }}>
            {cancelled ? 'Cancelado' : 'Agotado'}
          </div>
        )}
      </div>

      <div style={{ padding: '20px 20px 24px' }}>
        <div style={{
          fontFamily: 'JetBrains Mono, monospace', fontSize: 10, letterSpacing: 2,
          textTransform: 'uppercase', color: ACCENT, marginBottom: 8,
        }}>
          {fmtDate(show.event_datetime)}{show.venue_city ? ` · ${show.venue_city}` : ''}
        </div>
        <div style={{
          fontFamily: 'Archivo, sans-serif', fontSize: 20, fontWeight: 800,
          letterSpacing: '0.02em', textTransform: 'uppercase', color: '#FFF',
          lineHeight: 1.15, marginBottom: 6,
        }}>
          {show.title}
        </div>
        {show.subtitle && (
          <div style={{
            fontFamily: 'DM Sans, sans-serif', fontSize: 13,
            color: '#888', marginBottom: 12,
          }}>
            {show.subtitle}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14 }}>
          <div style={{
            fontFamily: 'Archivo, sans-serif', fontSize: 18, fontWeight: 800,
            color: '#f5f0e8',
          }}>
            {priceLabel(show)}
          </div>
          {!cancelled && !soldOut && (
            <span style={{
              fontFamily: 'JetBrains Mono, monospace', fontSize: 10,
              letterSpacing: 2, textTransform: 'uppercase', color: ACCENT, fontWeight: 700,
            }}>
              Entradas →
            </span>
          )}
        </div>
        {show.doors_open_at && !soldOut && !cancelled && (
          <div style={{
            marginTop: 10, fontFamily: 'JetBrains Mono, monospace',
            fontSize: 10, letterSpacing: 1.5, color: '#555',
          }}>
            Puertas · {fmtTime(show.doors_open_at)}
          </div>
        )}
      </div>
    </Link>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      fontFamily: 'DM Sans, system-ui, sans-serif',
      background: '#0a0a0a', color: '#f5f0e8', minHeight: '100vh',
    }}>
      <Nav />
      {children}
    </div>
  );
}

function Nav() {
  return (
    <nav style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '18px 32px',
      background: 'rgba(10,10,10,0.85)',
      backdropFilter: 'blur(10px)',
      borderBottom: '1px solid #1a1a1a',
    }}>
      <Link to="/" style={{
        display: 'flex', alignItems: 'center', gap: 10, color: '#FFF', textDecoration: 'none',
        fontFamily: 'Archivo, sans-serif', fontSize: 14, fontWeight: 900,
        letterSpacing: 2, textTransform: 'uppercase',
      }}>
        <span style={{ display: 'inline-block', width: 10, height: 10, background: ACCENT, borderRadius: '50%' }} />
        Pagafácil
      </Link>
      <div style={{ display: 'flex', gap: 24 }}>
        <Link to="/entradas" style={{ ...navLink, opacity: 1, borderBottom: `2px solid ${ACCENT}`, paddingBottom: 2 }}>
          Cartelera
        </Link>
        <Link to="/precio" style={navLink}>Precio</Link>
        <Link to="/mis-cobros" style={navLink}>Mis cobros</Link>
      </div>
    </nav>
  );
}

function DarkFooter() {
  return (
    <footer style={{
      padding: '32px 32px', borderTop: '1px solid #1a1a1a',
      background: '#0a0a0a', color: '#555',
      display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between',
      gap: 16, fontSize: 12, fontFamily: 'JetBrains Mono, monospace',
      letterSpacing: 1.5, textTransform: 'uppercase',
    }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        <span style={{ width: 8, height: 8, background: ACCENT, borderRadius: '50%' }} />
        Pagafácil · Sin app · Sin login
      </span>
      <span>
        <a href="mailto:hola@pagafacil.eu" style={{ color: '#888', textDecoration: 'none' }}>
          hola@pagafacil.eu
        </a>
      </span>
      <Link to="/" style={{ color: '#555', textDecoration: 'none' }}>Inicio</Link>
    </footer>
  );
}

const navLink: React.CSSProperties = {
  color: '#f5f0e8', textDecoration: 'none',
  fontFamily: 'JetBrains Mono, monospace', fontSize: 11,
  letterSpacing: 2, textTransform: 'uppercase',
  opacity: 0.75, transition: 'opacity 0.2s',
};
